/**
 * /invites/:token — invitee lands here. If signed out, send to login
 * preserving the URL. Once signed in, fetch a redacted, server-side preview
 * of the invite (account_invites is not client-readable), then gate acceptance
 * on (a) the signed-in email matching the invite and (b) that email being
 * VERIFIED. `accept_invite` enforces BOTH server-side; here we surface the
 * mismatch and drive the verify-your-email step for email/password invitees
 * (Google sign-ins are already verified). The verify gate is what stops a
 * leaked invite link from being accepted by someone who registered the invited
 * address without owning the inbox — see docs/security-hardening.md.
 */

import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';

import { acceptInvite, getInvitePreview } from '@/lib/callables';
import type { GetInvitePreviewResult } from '@/lib/callables';
import { useAuth } from '@/hooks/useAuth';
import { AuthLayout } from '@/components/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<GetInvitePreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifySent, setVerifySent] = useState(false);

  const signedIn = auth.state === 'signedIn';

  useEffect(() => {
    // The preview requires a signed-in caller; when signed out we redirect to
    // login (below) and re-run once we come back signed in.
    if (!token || !signedIn) return;
    getInvitePreview({ token })
      .then((res) => {
        setInvite(res.data);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not read invite.');
        setLoading(false);
      });
  }, [token, signedIn]);

  if (auth.state === 'loading') {
    return (
      <AuthLayout title="Invitation">
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (auth.state === 'signedOut') {
    const next = encodeURIComponent(`/invites/${token}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (loading) {
    return (
      <AuthLayout title="Invitation">
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (!invite) {
    return (
      <AuthLayout title="Invitation">
        <p className="text-sm text-ink-soft">
          {error ?? 'Invite not found. The link may have expired or been revoked.'}
        </p>
      </AuthLayout>
    );
  }

  const userEmail = auth.user.email ?? '';
  // Only walk a pending invite through the verify step — a matching-but-unverified
  // user on an expired/revoked/accepted invite shouldn't be sent round the
  // send-email → verify dance only to find Accept still disabled.
  const needsVerify =
    invite.emailMatches && !invite.emailVerified && invite.status === 'pending';
  const canAccept = invite.emailMatches && invite.emailVerified && invite.status === 'pending';

  // A non-pending invite is not acceptable, so say WHY rather than leaving the
  // Accept button disabled with no explanation (an expired invite that the user
  // followed after the 7-day TTL is the common case; the cleanup job flips it to
  // "expired" server-side). null while pending.
  const isPending = invite.status === 'pending';
  const statusNotice: string | null = isPending
    ? null
    : invite.status === 'expired'
      ? 'This invitation has expired. Please ask whoever invited you to send a fresh link.'
      : invite.status === 'revoked'
        ? 'This invitation has been withdrawn.'
        : invite.status === 'accepted'
          ? 'This invitation has already been accepted.'
          : 'This invitation is no longer valid.';

  const sendVerification = async () => {
    if (auth.state !== 'signedIn') return;
    setBusy(true);
    setError(null);
    try {
      // Round-trip the verification link back to this invite page so the user
      // lands where they can click "I've verified", not on Firebase's default
      // handler with no way back.
      await sendEmailVerification(auth.user, { url: window.location.href });
      setVerifySent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the verification email.');
    } finally {
      setBusy(false);
    }
  };

  const recheckVerified = async () => {
    if (auth.state !== 'signedIn' || !token) return;
    setBusy(true);
    setError(null);
    try {
      await auth.user.reload();
      // Force a fresh ID token so the callable/accept see the updated
      // email_verified claim (the cached token still says false).
      await auth.user.getIdToken(true);
      const res = await getInvitePreview({ token });
      setInvite(res.data);
      // Only nudge to keep verifying while the invite is still acceptable — if it
      // flipped to expired/revoked meanwhile, the status notice says so and a
      // "still not verified" error would just contradict it.
      if (res.data.status === 'pending' && !res.data.emailVerified) {
        setError('Still not verified — open the link in the email, then try again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="You've been invited">
      <p className="mb-6 text-sm text-ink-soft">
        You've been invited as a <span className="font-medium">{invite.role}</span> to help care
        for <span className="font-medium">{invite.elderName || 'this account'}</span>.
        {isPending && ' Accept to start managing events and buttons.'}
      </p>
      {statusNotice && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {statusNotice}
        </div>
      )}
      {isPending && !invite.emailMatches && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This invite was sent to <span className="font-mono">{invite.emailRedacted}</span>, but
          you're signed in as <span className="font-mono">{userEmail}</span>. Sign in with the
          right account to accept.
        </div>
      )}
      {needsVerify && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <p className="mb-2">
            Please verify your email before accepting — this confirms you own{' '}
            <span className="font-mono">{userEmail}</span>.
            {verifySent && ' We’ve sent a link; open it, then choose “I’ve verified”.'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={busy} onClick={sendVerification}>
              {verifySent ? 'Resend email' : 'Send verification email'}
            </Button>
            {verifySent && (
              <Button variant="ghost" disabled={busy} onClick={recheckVerified}>
                I've verified
              </Button>
            )}
          </div>
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      <div className="flex gap-2">
        {isPending ? (
          <>
            <Button
              disabled={busy || !canAccept}
              onClick={async () => {
                if (!token) return;
                setBusy(true);
                setError(null);
                try {
                  const result = await acceptInvite({ token });
                  navigate(`/accounts/${result.data.accountId}/profile`, { replace: true });
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Could not accept invite.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Accepting…' : 'Accept invite'}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              Decline
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            Back to dashboard
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
